import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Eye, Send, X, ArrowLeft, CheckCircle, FileText } from 'lucide-react';

const LPDConfirmation = ({ onClose, formData }) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [status, setStatus] = useState('');
  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("authToken");

  const getFormattedDates = () => {
    const today = new Date();
    const formattedToday = today.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 7);
    const formattedFutureDate = futureDate.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    return { formattedToday, formattedFutureDate };
  };

  const generatePDFContent = () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const blackColor = [0, 0, 0];
    const lightGreyColor = [240, 240, 240];
    const { formattedToday } = getFormattedDates();
    const margin = 20;
    const contentWidth = 170;
    let yPos = margin;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Company name", margin, yPos);
    pdf.text("Solicitors", margin + contentWidth / 2, yPos);
    yPos += 10;
    pdf.setDrawColor(lightGreyColor[0], lightGreyColor[1], lightGreyColor[2]);
    pdf.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 20;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(blackColor[0], blackColor[1], blackColor[2]);
    pdf.text(`${formData.name || 'N/A'}`, margin, yPos); yPos += 5;
    if (formData.businessName) { pdf.text(`${formData.businessName}`, margin, yPos); yPos += 5; }
    const addressParts = [formData.houseName, formData.street, formData.city, formData.country, formData.postcode].filter(Boolean);
    if (addressParts.length) { pdf.text(addressParts.join(", "), margin, yPos); yPos += 5; }
    yPos += 10;
    pdf.text(formattedToday, margin, yPos); yPos += 15;
    pdf.text(`Dear ${formData.name || 'Sirs'}`, margin, yPos); yPos += 10;

    pdf.setFont("helvetica", "bold");
    pdf.text(`Re: Invoice Number: ${formData.invoiceNumber || 'Not found'}`, margin, yPos); yPos += 15;

    pdf.setFont("helvetica", "bold");
    const noticeHeader = "NOTICE OF ATTENTION TO COMMENCE LEGAL PROCEEDINGS pursuant to the Late Payment of Commercial Debts (Interest) Act 1998 as amended and supplemented by the Late Payment of Commercial Debt Regulations 2002 and 2013.";
    const wrappedNotice = pdf.splitTextToSize(noticeHeader, contentWidth);
    pdf.text(wrappedNotice, margin, yPos); yPos += wrappedNotice.length * 5 + 10;

    pdf.setFont("helvetica", "normal");
    const firstParagraph = "We hereby give you notice that our client intends to commence Country Court proceedings which will include a claim for late payment compensation, accrued interest and the reasonable cost of this Solicitor's Letter.";
    const wrapped1 = pdf.splitTextToSize(firstParagraph, contentWidth);
    pdf.text(wrapped1, margin, yPos); yPos += wrapped1.length * 5 + 10;

    const amount = formData.amount ? parseFloat(formData.amount) : 0;
    const latePaymentCompensation = amount <= 999.99 ? 40 : amount <= 9999.99 ? 70 : 100;
    const interest = amount > 0 ? (amount * 0.08 * 30 / 365).toFixed(2) : 0;
    const solicitorFee = 25.00;

    pdf.text(`The sum now owing to our client is £ ${amount || '3466'} together with late payment compensation of`, margin, yPos); yPos += 5;
    pdf.text(`£${latePaymentCompensation}.00, interest of £${interest} and £${solicitorFee}.00 reasonable Solicitor's Letter costs claimable under`, margin, yPos); yPos += 5;
    pdf.text(`Regulation 3 of the Late Payment of Commercial Debt Regulations 2013.`, margin, yPos); yPos += 10;
    pdf.text(`Take notice that unless our client receives payment for the total sum of £ ${amount || '3466'} within 7 days`, margin, yPos); yPos += 10;
    pdf.text(`from the date of this letter proceedings will be issued for the whole balance outstanding`, margin, yPos); yPos += 5;
    pdf.text(`together with continuing interest and Court costs without further notice to you.`, margin, yPos); yPos += 15;
    pdf.text("Yours faithfully,", margin, yPos); yPos += 15;

    if (formData.signatureImage) {
      pdf.addImage(formData.signatureImage, 'PNG', margin, yPos, 50, 15); yPos += 20;
    } else {
      pdf.setDrawColor(blackColor[0], blackColor[1], blackColor[2]);
      pdf.setFillColor(lightGreyColor[0], lightGreyColor[1], lightGreyColor[2]);
      pdf.roundedRect(margin, yPos, 50, 15, 2, 2, 'FD');
      pdf.setFontSize(9); pdf.text("Signature", margin + 15, yPos + 8); yPos += 20;
    }

    pdf.setFontSize(10);
    pdf.text("Company name", margin, yPos);

    return pdf;
  };

  const previewPDF = useCallback(() => {
    try {
      setIsGenerating(true);
      setStatus('Generating preview…');
      const pdf = generatePDFContent();
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(url);
      setIsPreviewMode(true);
      setIsGenerating(false);
      setStatus('');
    } catch (error) {
      console.error('Preview error:', error);
      setIsGenerating(false);
      setStatus('');
      alert('Failed to generate preview. Please try again.');
    }
  }, [formData]);

  const generatePDF = useCallback(async () => {
    try {
      setIsGenerating(true);
      setStatus('Generating PDF…');
      const pdf = generatePDFContent();
      const pdfFilename = `LPD_Form_${formData.name ? formData.name.replace(/\s+/g, '_') : 'document'}_${formData.invoiceNumber || 'invoice'}.pdf`;
      const pdfFile = new File([pdf.output('blob')], pdfFilename, { type: 'application/pdf' });

      setStatus('Sending email…');
      const emailFormData = new FormData();
      emailFormData.append('pdfFile', pdfFile);
      emailFormData.append('recipientEmail', formData.email || '');
      emailFormData.append('recipientName', formData.name || '');
      emailFormData.append('subject', `Notice of Intention to Commence Legal Proceedings – Invoice ${formData.invoiceNumber || 'N/A'}`);
      emailFormData.append('businessName', formData.businessName || '');
      emailFormData.append('amount', formData.amount || '');
      emailFormData.append('currency', formData.currency || '£');
      emailFormData.append('invoiceNumber', formData.invoiceNumber || '');
      if (formData.signatureImage) emailFormData.append('hasSignature', 'true');

      const response = await fetch(`${API_URL}/api/lpd/send-lpd-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: emailFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }

      setIsGenerating(false);
      setStatus('');
      alert(`Email successfully sent to ${formData.email}`);
      navigate('/lpd');
    } catch (error) {
      console.error('Error:', error);
      setIsGenerating(false);
      setStatus('');
      alert('Operation failed. Please try again. ' + error.message);
    }
  }, [formData, navigate, API_URL]);

  const closePreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setIsPreviewMode(false);
  };

  const detailRows = [
    { label: "Recipient",      value: formData.name          || "Not provided" },
    { label: "Business",       value: formData.businessName  || "Not provided" },
    { label: "Email",          value: formData.email         || "Not provided" },
    { label: "Invoice number", value: formData.invoiceNumber || "Not provided" },
    { label: "Amount due",     value: formData.amount ? `${formData.currency || '£'} ${formData.amount}` : "Not provided", highlight: true },
    { label: "Due date",       value: formData.dueDate ? formData.dueDate.toLocaleDateString("en-GB") : "Not provided" },
  ];

  // ── Preview mode ──────────────────────────────────────────────────────────
  if (isPreviewMode && pdfPreviewUrl) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
          .lpd-conf * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
        `}</style>
        <div className="lpd-conf" style={{ width: "90vw", height: "90vh", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", border: "1px solid #E2E8F0", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden" }}>

          {/* Header */}
          <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFD" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #BFDBFE" }}>
                <FileText size={18} style={{ color: "#2563EB" }} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>PDF Preview</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Invoice {formData.invoiceNumber || "N/A"} · {formData.name || "—"}</div>
              </div>
            </div>
            <button onClick={closePreview} style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#ffffff", border: "1px solid #E2E8F0", cursor: "pointer" }}>
              <X size={15} style={{ color: "#64748B" }} />
            </button>
          </div>

          {/* PDF iframe */}
          <div style={{ flex: 1, overflow: "hidden", padding: 16 }}>
            <iframe src={pdfPreviewUrl} style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }} title="PDF Preview" />
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 24px", borderTop: "1px solid #E2E8F0", background: "#FAFBFD", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
            <button
              onClick={closePreview}
              disabled={isGenerating}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: 8, fontFamily: "DM Sans,sans-serif", fontSize: 13, fontWeight: 600, color: "#1E293B", cursor: "pointer" }}
            >
              <ArrowLeft size={14} /> Back to edit
            </button>
            <button
              onClick={generatePDF}
              disabled={isGenerating}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", background: isGenerating ? "#93C5FD" : "linear-gradient(135deg,#2563EB,#1D4ED8)", border: "none", borderRadius: 10, fontFamily: "DM Sans,sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", cursor: isGenerating ? "not-allowed" : "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
            >
              {isGenerating ? (
                <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />{status || "Sending…"}</>
              ) : (
                <><Send size={14} />Send email</>
              )}
            </button>
          </div>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </>
    );
  }

  // ── Confirmation mode ─────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
        .lpd-conf * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="lpd-conf" style={{ background: "#fff", borderRadius: 16, border: "1px solid #E2E8F0", boxShadow: "0 20px 60px rgba(0,0,0,0.12)", width: 480, overflow: "hidden" }}>

        {/* Modal header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #BBF7D0", flexShrink: 0 }}>
              <CheckCircle size={20} style={{ color: "#059669" }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>LPD form ready</div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                {status || "Review details below before sending."}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#ffffff", border: "1px solid #E2E8F0", cursor: "pointer", flexShrink: 0 }}
          >
            <X size={14} style={{ color: "#64748B" }} />
          </button>
        </div>

        {/* Detail rows */}
        <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
          {detailRows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "11px 0",
                borderBottom: i < detailRows.length - 1 ? "1px solid #F1F5F9" : "none",
              }}
            >
              <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {row.label}
              </span>
              <span style={{
                fontSize: 13, fontWeight: row.highlight ? 700 : 500,
                color: row.highlight ? "#DC2626" : "#0F172A",
                fontFamily: row.highlight ? "'DM Mono', monospace" : "inherit",
                maxWidth: 260, textAlign: "right",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Footer buttons */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #E2E8F0", background: "#FAFBFD", display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={onClose}
            disabled={isGenerating}
            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 14px", background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: 8, fontFamily: "DM Sans,sans-serif", fontSize: 13, fontWeight: 600, color: "#1E293B", cursor: isGenerating ? "not-allowed" : "pointer", opacity: isGenerating ? 0.5 : 1 }}
          >
            Cancel
          </button>

          <button
            onClick={previewPDF}
            disabled={isGenerating}
            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 14px", background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: 8, fontFamily: "DM Sans,sans-serif", fontSize: 13, fontWeight: 600, color: "#1E293B", cursor: isGenerating ? "not-allowed" : "pointer", opacity: isGenerating ? 0.5 : 1 }}
          >
            {isGenerating && status.includes("preview") ? (
              <span style={{ width: 14, height: 14, border: "2px solid #E2E8F0", borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
            ) : <Eye size={14} />}
            Preview PDF
          </button>

          <button
            onClick={generatePDF}
            disabled={isGenerating}
            style={{
              flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "9px 14px", border: "none", borderRadius: 8,
              background: isGenerating ? "#93C5FD" : "linear-gradient(135deg,#2563EB,#1D4ED8)",
              fontFamily: "DM Sans,sans-serif", fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: isGenerating ? "not-allowed" : "pointer",
              boxShadow: isGenerating ? "none" : "0 2px 8px rgba(37,99,235,0.3)",
            }}
          >
            {isGenerating && !status.includes("preview") ? (
              <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />{status || "Sending…"}</>
            ) : (
              <><Send size={14} />Send email</>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default LPDConfirmation;